import React, { useState } from 'react';
import clsx from 'clsx';

import {
  Accordion, Icon, Skeleton, Typography
} from 'wtb-components';
import { useAppContext } from 'context';
import uiIcons from 'uiIcons';
import styles from './Block.module.css';

const Title = (props) => {
  const {
    active,
    async,
    children,
    circleContent,
    disabled,
    main,
    hasAction,
    actionText,
    className,
    ...other
  } = props;

  const { isLoading, isSuccess } = useAppContext();

  const checkComponent = <Icon name={uiIcons.check} className={styles.check} />;

  const isActive = !disabled && active && !isSuccess();

  return (
    <div
      className={clsx(className, {
        [styles.mainBlockTitle]: main,
        [styles.mainBlockTitleActive]: main && isActive,
        [styles.mainBlockTitleCollapsed]: main && !isActive && !disabled,
        [styles.mainBlockTitleDisabled]: main && disabled,
        [styles.mainBlockTitleWithAction]: main && !disabled && hasAction
      })}
      {...other}
    >
      {main && (
        <div className={clsx({ [styles.circle]: circleContent, [styles.circleDisabled]: circleContent && disabled })}>
          {circleContent === 'check' ? checkComponent : circleContent}
        </div>
      )}
      <Typography
        className={styles.title}
        fontWeight={isActive ? 700 : 500}
        component="div"
        variant="subtitle1"
      >
        {async && isLoading ? <Skeleton width="80%" /> : children}
      </Typography>

      {hasAction && (
        <Typography
          className={styles.actionButton}
          component="span"
          variant="caption"
        >
          {actionText}
        </Typography>
      )}
    </div>
  );
};

const BlockSkeleton = () => (
  <div data-testid="block-skeleton">
    <Skeleton width="50%" />
    <Skeleton variant="rect" height={250} />
  </div>
);

const Block = (props) => {
  const { isLoading, isSuccess } = useAppContext();

  const {
    id,
    asyncTitle,
    children,
    className,
    collapsable = true,
    disabled,
    defaultExpanded,
    expanded,
    headerBorder,
    headerClass,
    main,
    checked,
    position,
    paddedBody,
    title,
    titleProps,
    hasAction,
    actionHandler,
    actionText
  } = props;

  const [ _isExpanded, setIsExpanded ] = useState(
    expanded !== undefined
      ? expanded
      : collapsable
        ? !!defaultExpanded
        : true
  );
  const isExpanded = expanded !== undefined ? expanded : _isExpanded;

  const getCircleContent = () => {
    if (checked) return 'check';
    else if (position) return position;
    else return undefined;
  };

  const handleOnChange = (_item) => { setIsExpanded(_item?.expanded); };

  const headerProps = hasAction && actionHandler && typeof actionHandler === 'function'
    ? {
      tabIndex: 0,
      role: 'button',
      onClick: actionHandler,
      onKeyPress: actionHandler
    }
    : {};

  return (
    <div className={clsx(styles.root, {
      [styles.rootMain]: main,
      [styles.rootMainCollapsed]: main && !isExpanded
    })}
    >
      {isLoading
        ? <BlockSkeleton />
        : (
          <Accordion
            className={clsx(styles.accordion, className)}
            data-testid="block-accordion"
            expanded={isExpanded}
            defaultExpanded={defaultExpanded}
            icon={!!collapsable}
            onChange={handleOnChange}
            header={(
              <Title
                main={main}
                active={isExpanded}
                async={!!asyncTitle}
                disabled={disabled}
                circleContent={getCircleContent()}
                hasAction={hasAction}
                actionText={actionText}
                {...titleProps}
              >
                {title || asyncTitle}
              </Title>
            )}
            HeaderProps={{
              className: clsx(
                styles.header,
                {
                  [styles.uncollapsable]: !collapsable && isExpanded,
                  [styles.mainBlockHeader]: main,
                  [styles.mainDisabled]: main && disabled,
                  [styles.mainCollapsed]: main && !disabled && !isExpanded && !isSuccess(),
                  [styles.mainExpanded]: main && !disabled && isExpanded && !isSuccess(),
                  [styles.mainSummary]: main && !disabled && isSuccess(),
                  [styles.headerBorder]: headerBorder && isExpanded,
                  [styles.headerWithAction]: hasAction
                },
                headerClass
              ),
              id: id || `block-${title || asyncTitle}`,
              'aria-label': id || `block-${title || asyncTitle}`,
              ...headerProps
            }}
            BodyProps={{ className: clsx(styles.body, { [styles.paddedBody]: paddedBody }) }}
          >
            {children}
          </Accordion>
        )}
    </div>
  );
};

export default Block;
